import {
  type DataColumn,
  DataTable,
  type LocalDataColumn,
  StatusBadge,
  Tabs,
  TabsList,
  TabsTrigger,
  TOUCH,
  type Tone,
  useDataGrid,
} from "@nymbl/nos2-rules";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { CalendarClock, History } from "lucide-react";
import { SettingsPage } from "@/components/layout/SettingsPage";
import { type JobRunRow, type JobView, useJobRuns, useJobs } from "@/hooks/use-jobs";
import { JOB_RUNS_HELP, JOBS_HELP } from "@/lib/column-help";
import {
  JOB_MODE_OPTIONS,
  JOB_RUN_STATUS_OPTIONS,
  JOBS_PAGE_VIEW_LABELS,
  JOBS_PAGE_VIEWS,
  type JobsPageView,
} from "@/lib/enums";
import { formatUtc, runResult } from "@/lib/jobs/format";
import { JOB_LABELS, JOB_NAMES, type JobName } from "@/lib/jobs/schedule";

const TONES: Record<string, Tone> = {
  running: "info",
  done: "success",
  failed: "danger",
  gave_up: "danger",
  skipped: "warning",
  off: "neutral",
  "dry-run": "info",
  live: "success",
};
const LABELS: Record<string, string> = Object.fromEntries(
  [...JOB_RUN_STATUS_OPTIONS, ...JOB_MODE_OPTIONS].map((o) => [o.value, o.label]),
);

/** A run's status or a job's mode, always with its words. */
function JobStatus({ status }: { status: string }) {
  return <StatusBadge status={status} tones={TONES} labels={LABELS} />;
}

const JOB_OPTIONS = JOB_NAMES.map((job) => ({ value: job, label: JOB_LABELS[job].label }));
const jobLabel = (job: string) =>
  (JOB_NAMES as readonly string[]).includes(job) ? JOB_LABELS[job as JobName].label : job;

/** A UTC time as a sortable, filterable day; the cell shows the full time. */
const day = (at: string | null | undefined) => (at ? at.slice(0, 10) : null);

const Result = ({ text, failed }: { text: string; failed: boolean }) =>
  text ? (
    <span
      className={`block whitespace-normal text-sm ${failed ? "text-destructive" : "text-muted-foreground"}`}
    >
      {text}
    </span>
  ) : (
    <span className="text-muted-foreground">—</span>
  );

export const JOB_COLUMNS: LocalDataColumn<JobView>[] = [
  {
    key: "job",
    label: "Job",
    type: "text",
    accessor: (j) => j.label,
    help: JOBS_HELP.job,
    render: (j) => <span className="font-medium">{j.label}</span>,
  },
  {
    key: "mode",
    label: "Mode",
    type: "enum",
    accessor: (j) => j.mode,
    options: [...JOB_MODE_OPTIONS],
    help: JOBS_HELP.mode,
    render: (j) => <JobStatus status={j.mode} />,
  },
  {
    key: "schedule",
    label: "Schedule",
    type: "text",
    accessor: (j) => j.schedule,
    help: JOBS_HELP.schedule,
  },
  {
    key: "lastRun",
    label: "Last run",
    type: "date",
    accessor: (j) => day(j.lastRun?.slot),
    help: JOBS_HELP.lastRun,
    searchText: (j) => (j.lastRun ? (LABELS[j.lastRun.status] ?? j.lastRun.status) : "No runs yet"),
    render: (j) =>
      j.lastRun ? (
        <span className="flex flex-wrap items-center gap-2">
          <JobStatus status={j.lastRun.status} />
          <span className="text-muted-foreground">{formatUtc(j.lastRun.slot)}</span>
        </span>
      ) : (
        <span className="text-muted-foreground">No runs yet</span>
      ),
    exportValue: (j) =>
      j.lastRun
        ? `${LABELS[j.lastRun.status] ?? j.lastRun.status}, ${formatUtc(j.lastRun.slot)}`
        : "",
  },
  {
    key: "lastResult",
    label: "Last result",
    type: "text",
    accessor: (j) => (j.lastRun ? runResult(j.lastRun) : ""),
    help: JOBS_HELP.lastResult,
    className: "min-w-[320px]",
    render: (j) =>
      j.lastRun ? (
        <Result text={runResult(j.lastRun)} failed={Boolean(j.lastRun.error)} />
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: "nextRun",
    label: "Next run",
    type: "date",
    accessor: (j) => day(j.nextRun),
    help: JOBS_HELP.nextRun,
    render: (j) => (
      <span className="text-muted-foreground">{j.nextRun ? formatUtc(j.nextRun) : "—"}</span>
    ),
    exportValue: (j) => (j.nextRun ? formatUtc(j.nextRun) : ""),
  },
];

export const JOB_RUN_COLUMNS: DataColumn<JobRunRow>[] = [
  {
    key: "job",
    label: "Job",
    type: "enum",
    accessor: (r) => r.job,
    options: JOB_OPTIONS,
    help: JOB_RUNS_HELP.job,
    render: (r) => <span className="font-medium">{jobLabel(r.job)}</span>,
    exportValue: (r) => jobLabel(r.job),
  },
  {
    key: "slot",
    label: "Slot",
    type: "date",
    accessor: (r) => day(r.slot),
    help: JOB_RUNS_HELP.slot,
    render: (r) => <span className="text-muted-foreground">{formatUtc(r.slot)}</span>,
    exportValue: (r) => r.slot,
  },
  {
    key: "status",
    label: "Status",
    type: "enum",
    accessor: (r) => r.status,
    options: [...JOB_RUN_STATUS_OPTIONS],
    help: JOB_RUNS_HELP.status,
    render: (r) => <JobStatus status={r.status} />,
  },
  {
    key: "result",
    label: "Result",
    type: "text",
    accessor: (r) => runResult(r),
    help: JOB_RUNS_HELP.result,
    className: "min-w-[320px]",
    // Counts are a per-run JSON object: the server can't sort or filter by them. The search
    // still matches the error text.
    sortable: false,
    filterable: false,
    render: (r) => <Result text={runResult(r)} failed={Boolean(r.error)} />,
  },
  {
    key: "attempts",
    label: "Attempts",
    type: "number",
    accessor: (r) => r.attempts,
    help: JOB_RUNS_HELP.attempts,
  },
  {
    key: "startedAt",
    label: "Started",
    type: "date",
    accessor: (r) => day(r.startedAt),
    help: JOB_RUNS_HELP.startedAt,
    render: (r) => <span className="text-muted-foreground">{formatUtc(r.startedAt)}</span>,
    exportValue: (r) => r.startedAt,
  },
  {
    key: "finishedAt",
    label: "Finished",
    type: "date",
    accessor: (r) => day(r.finishedAt),
    help: JOB_RUNS_HELP.finishedAt,
    // Empty while a run is running, and keyset paging can't sort on a column that can be empty
    // (docs/data/tables.md rule 4). It still filters by date on the server.
    sortable: false,
    render: (r) => (
      <span className="text-muted-foreground">{r.finishedAt ? formatUtc(r.finishedAt) : "—"}</span>
    ),
    exportValue: (r) => r.finishedAt ?? "",
  },
];

/** The element the tabs switch (the tabs have no TabsContent). */
const VIEW_ID = "jobs-view";

function JobsTable() {
  const jobs = useJobs();
  return (
    <DataTable<JobView>
      label="Jobs"
      title="Jobs"
      rows={jobs.data?.data ?? []}
      rowKey={(j) => j.job}
      columns={JOB_COLUMNS}
      search={{ placeholder: "Search jobs…" }}
      loading={jobs.isLoading}
      error={jobs.error}
      onRetry={() => void jobs.refetch()}
      empty={{ title: "No jobs", icon: CalendarClock }}
    />
  );
}

function RunHistoryTable() {
  const grid = useDataGrid({ defaultSort: "slot", storageKey: "dashboard:job-runs:v1" });
  return (
    <DataTable<JobRunRow>
      label="Run history"
      title="Run history"
      useRows={useJobRuns}
      grid={grid}
      rowKey={(r) => r.id}
      columns={JOB_RUN_COLUMNS}
      defaultSort="slot"
      defaultSortDir="desc"
      search={{ placeholder: "Search runs…" }}
      itemLabel="runs"
      empty={{ title: "No runs yet", icon: History }}
    />
  );
}

/**
 * Settings, Jobs: each scheduled job with its mode, schedule, last and next run, and the run
 * history. Read-only: modes come from the environment's switches (docs/operations.md).
 */
export function JobsPanel() {
  const search = useSearch({ from: "/dashboard/settings/jobs" });
  const navigate = useNavigate();
  const view: JobsPageView = search.view === "history" ? "history" : "jobs";
  return (
    <SettingsPage>
      <div className="flex w-full flex-col gap-5">
        <Tabs
          value={view}
          onValueChange={(v) =>
            void navigate({
              to: "/dashboard/settings/jobs",
              search: { view: v === "history" ? "history" : undefined },
              replace: true,
            })
          }
        >
          <TabsList aria-label="Jobs view">
            {JOBS_PAGE_VIEWS.map((v) => (
              <TabsTrigger key={v} value={v} className={TOUCH} aria-controls={VIEW_ID}>
                {JOBS_PAGE_VIEW_LABELS[v]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div id={VIEW_ID}>{view === "history" ? <RunHistoryTable /> : <JobsTable />}</div>
      </div>
    </SettingsPage>
  );
}
